import { useEffect, useState } from 'react';
import { CreditCard, MailCheck } from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { InlineSelect } from '@/components/ui/InlineSelect';
import { InvoiceStatusBadge } from '@/components/modules/InvoiceStatusBadge';
import { useFinanceStore } from '@/stores/financeStore';
import { formatCurrency, formatDate, formatRelative } from '@/lib/format';
import type { Invoice } from '@nexus/shared/types';

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'void', label: 'Void' },
];

interface InvoiceSheetProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

export function InvoiceSheet({ id, open, onClose }: InvoiceSheetProps) {
  const { invoices, update, markPaid, followUp } = useFinanceStore();
  const [actionLoading, setActionLoading] = useState(false);

  // Find invoice from local store (no fetchOne in financeStore)
  const invoice: Invoice | undefined = invoices.find((i) => i.id === id);

  if (!invoice) {
    return (
      <Sheet open={open} onClose={onClose} title="Invoice">
        <div className="flex items-center justify-center py-12 text-surface-500">Loading...</div>
      </Sheet>
    );
  }

  const canAct = !['paid', 'void', 'draft'].includes(invoice.status);
  const canFollowUp = ['sent', 'overdue', 'viewed'].includes(invoice.status);

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try { await markPaid(invoice.id); } finally { setActionLoading(false); }
  };

  const handleFollowUp = async () => {
    setActionLoading(true);
    try { await followUp(invoice.id); } finally { setActionLoading(false); }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Invoice Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-lg font-mono font-semibold text-nexus-400">{invoice.invoiceNumber}</p>
            <p className="text-2xl font-bold text-surface-100 mt-1">
              {formatCurrency(invoice.amount, invoice.currency)}
            </p>
          </div>
          <InlineSelect
            value={invoice.status}
            options={statusOptions}
            onSave={async (v) => { await update(invoice.id, { status: v }); }}
          />
        </div>

        {/* Details */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Details</h3>
          <div className="grid grid-cols-[120px_1fr] gap-y-3 items-center">
            <span className="text-xs text-surface-500">Currency</span>
            <span className="text-sm text-surface-300">{invoice.currency}</span>

            <span className="text-xs text-surface-500">Due Date</span>
            <span className="text-sm text-surface-300">{formatDate(invoice.dueDate)}</span>

            <span className="text-xs text-surface-500">Paid Date</span>
            <span className="text-sm text-surface-300">
              {invoice.paidDate ? formatDate(invoice.paidDate) : '--'}
            </span>

            <span className="text-xs text-surface-500">Follow-ups</span>
            <span className={`text-sm font-medium ${invoice.followUpCount > 2 ? 'text-red-400' : 'text-surface-300'}`}>
              {invoice.followUpCount}
            </span>

            <span className="text-xs text-surface-500">Last Follow-up</span>
            <span className="text-sm text-surface-300">
              {invoice.lastFollowUpAt ? formatRelative(invoice.lastFollowUpAt) : '--'}
            </span>

            <span className="text-xs text-surface-500">Created</span>
            <span className="text-sm text-surface-300">{formatRelative(invoice.createdAt)}</span>
          </div>
        </div>

        {/* Line Items */}
        {invoice.lineItems && invoice.lineItems.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Line Items</h3>
            <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-700">
                    <th className="px-3 py-2 text-left text-xs font-medium text-surface-500">Description</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-surface-500">Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-surface-500">Unit Price</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-surface-500">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-700/50">
                  {invoice.lineItems.map((item, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 text-sm text-surface-200">{item.description}</td>
                      <td className="px-3 py-2 text-sm text-surface-300 text-right">{item.quantity}</td>
                      <td className="px-3 py-2 text-sm text-surface-300 text-right">
                        {formatCurrency(item.unitPrice, invoice.currency)}
                      </td>
                      <td className="px-3 py-2 text-sm text-surface-200 text-right font-medium">
                        {formatCurrency(item.total, invoice.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-surface-600">
                    <td colSpan={3} className="px-3 py-2 text-sm text-surface-400 text-right font-medium">
                      Total
                    </td>
                    <td className="px-3 py-2 text-sm text-surface-100 text-right font-semibold">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        {canAct && (
          <div className="flex items-center gap-2 pt-2 border-t border-surface-700">
            <Button
              variant="primary"
              size="sm"
              icon={<CreditCard className="h-3.5 w-3.5" />}
              onClick={handleMarkPaid}
              loading={actionLoading}
            >
              Mark Paid
            </Button>
            {canFollowUp && (
              <Button
                variant="secondary"
                size="sm"
                icon={<MailCheck className="h-3.5 w-3.5" />}
                onClick={handleFollowUp}
                loading={actionLoading}
              >
                Send Follow-up
              </Button>
            )}
          </div>
        )}
      </div>
    </Sheet>
  );
}
